import { ArrowDownTrayIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { sumBy, TotalsRow } from 'components/TableTotals'
import { usePermissions } from 'hooks/usePermissions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  applyEscrowAnalysis,
  createLoanVoucher,
  deactivateLoanVoucher,
  downloadEscrowStatement,
  getEscrowAnalyses,
  getEscrowAnalysisDefaults,
  getLoanVoucherProjection,
  getLoanVouchers,
  updateLoanVoucher,
} from 'services'
import { Button, Input2, Modal, Select2 } from 'stories/components'
import { confirm, formatDate, thousandSeperatorWithPrefix } from 'utils'

import { EscrowAnalysisDialog } from './EscrowAnalysisDialog'

/** An obligation on the loan — taxes, insurance, HOA — with what is owed and when. */
interface Voucher {
  id: number
  type: string
  payeeName: string
  reference: string
  amountDue: number
  dueDate: string
  recurring: boolean
  frequency: string
  escrowed: boolean
  active: boolean
  notes: string
}

interface Projected {
  voucherId: number
  type: string
  payeeName: string
  date: string
  amount: number
}

const TYPES: Record<string, string> = {
  Taxes: 'Taxes',
  'Hazard Insurance': 'Hazard Insurance',
  'Flood Insurance': 'Flood Insurance',
  'Mortgage Insurance': 'Mortgage Insurance',
  'HOA Dues': 'HOA Dues',
  'Ground Rent': 'Ground Rent',
  Other: 'Other',
}

const FREQUENCIES: Record<string, string> = {
  Annual: 'Annual',
  Semiannual: 'Semiannual',
  Quarterly: 'Quarterly',
  Monthly: 'Monthly',
}

const emptyVoucher = (): Partial<Voucher> => ({
  type: 'Taxes',
  payeeName: '',
  reference: '',
  amountDue: 0,
  dueDate: '',
  recurring: true,
  frequency: 'Annual',
  escrowed: true,
})

export default function Vouchers() {
  const [vouchers, setVouchers] = useState<Voucher[]>([])
  const [projection, setProjection] = useState<{ disbursements: Projected[]; total: number }>({
    disbursements: [],
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Partial<Voucher> | null>(null)
  const [saving, setSaving] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyses, setAnalyses] = useState<any[]>([])
  // The balance and payment an analysis starts from, as Servicing Information reports them. The
  // dialog opens on these rather than on zeros, which no real loan ever has.
  const [escrowDefaults, setEscrowDefaults] = useState({ startingBalance: 0, currentMonthlyEscrow: 0 })
  const [downloading, setDownloading] = useState(0)
  // The analysis being applied and the payment due date its new escrow takes effect with.
  const [applying, setApplying] = useState<{ id: number; effectiveDate: string } | null>(null)
  const [applyBusy, setApplyBusy] = useState(false)
  const { hasPermission } = usePermissions()
  const canApply = hasPermission('Loan.Servicing.ServicingInformation.Payments.Edit')

  // The projection window opens on the first of next month, which is where a computation year
  // normally starts. It is only a preview here — the escrow analysis owns the real year.
  const projectionStart = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [list, proj, runs, defaults]: any[] = await Promise.all([
        getLoanVouchers(),
        getLoanVoucherProjection({ startDate: projectionStart, months: 12 }),
        getEscrowAnalyses(),
        getEscrowAnalysisDefaults(),
      ])
      setAnalyses(Array.isArray(runs?.items) ? runs.items : [])
      setEscrowDefaults({
        startingBalance: Number(defaults?.startingBalance) || 0,
        currentMonthlyEscrow: Number(defaults?.currentMonthlyEscrow) || 0,
      })
      setVouchers(Array.isArray(list?.items) ? list.items : [])
      setProjection({
        disbursements: Array.isArray(proj?.disbursements) ? proj.disbursements : [],
        total: Number(proj?.total) || 0,
      })
    } catch {
    } finally {
      setLoading(false)
    }
  }, [projectionStart])

  useEffect(() => {
    load()
  }, [load])

  const onSubmit = async () => {
    if (!editing) return
    setSaving(true)
    try {
      const res: any = editing.id
        ? await updateLoanVoucher(editing.id, editing)
        : await createLoanVoucher(editing)
      if (res?.success) {
        toast(editing.id ? 'Voucher updated.' : 'Voucher added.', { type: 'success' })
        setEditing(null)
        load()
      } else {
        toast(res?.message || 'Could not save this voucher.', { type: 'error' })
      }
    } finally {
      setSaving(false)
    }
  }

  const onDeactivate = async (v: Voucher) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Deactivate <span className="font-semibold">{v.type}</span>?
        <p className="text-sm mt-2">
          It stops projecting into future escrow analyses. The record is kept so an analysis for an
          earlier year can still be reproduced.
        </p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await deactivateLoanVoucher(v.id)
    if (res?.success) {
      toast('Voucher deactivated.', { type: 'success' })
      load()
    } else {
      toast(res?.message || 'Could not deactivate this voucher.', { type: 'error' })
    }
  }

  /** The borrower's disclosure statement for that run. */
  const onDownloadStatement = async (id: number) => {
    setDownloading(id)
    try {
      await downloadEscrowStatement(id)
    } catch {
      toast('Could not produce the statement.', { type: 'error' })
    } finally {
      setDownloading(0)
    }
  }

  /**
   * Apply an analysis: its new monthly escrow is scheduled on Rate & Payment Changes, effective with
   * the chosen payment due date. Saving an analysis never changes the loan's escrow on its own.
   */
  const onApply = async () => {
    if (!applying?.effectiveDate) return
    setApplyBusy(true)
    try {
      await applyEscrowAnalysis(applying.id, applying.effectiveDate)
      toast('New escrow scheduled. See Rate & Payment Changes.', { type: 'success' })
      setApplying(null)
      load()
    } catch (err: any) {
      toast(err?.message || 'Could not apply the analysis.', { type: 'error' })
    } finally {
      setApplyBusy(false)
    }
  }

  const set = (key: string, value: any) => setEditing((prev) => ({ ...(prev || {}), [key]: value }))

  const monthly = projection.total / 12

  return (
    <div className="Vouchers-container relative">
      <LayoutLoading show={loading} />

      <div className="flex justify-between items-center mb-3">
        <div>
          <p className="font-semibold text-[16px]">Escrow Vouchers</p>
          <p className="text-sm text-gray-500">
            What this loan owes out — taxes, insurance, association dues — with the amount and date
            each comes due. Escrowed items feed the escrow analysis.
          </p>
        </div>
        <span className="flex gap-2">
          <Button color="gray" onClick={() => setAnalyzing(true)}>
            Run Escrow Analysis
          </Button>
          <Button onClick={() => setEditing(emptyVoucher())}>
            <span className="flex items-center gap-1">
              <PlusIcon className="w-4 h-4" /> Add Voucher
            </span>
          </Button>
        </span>
      </div>

      <table className="w-full text-[14px] text-left text-gray-900">
        <thead className="bg-gray-100 text-gray-700 uppercase text-[13px]">
          <tr>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Payee</th>
            <th className="px-3 py-2">Reference</th>
            <th className="px-3 py-2 text-right">Amount Due</th>
            <th className="px-3 py-2">Date Due</th>
            <th className="px-3 py-2">Recurring</th>
            <th className="px-3 py-2">Escrowed</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {vouchers.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={8}>
                {loading ? 'Loading…' : 'No vouchers on this loan yet.'}
              </td>
            </tr>
          )}
          {vouchers.map((v) => (
            <tr key={v.id} className={`border-b ${v.active ? '' : 'opacity-50'}`}>
              <td className="px-3 py-2">
                {v.type}
                {!v.active && <span className="ml-2 text-[11px] uppercase text-gray-500">inactive</span>}
              </td>
              <td className="px-3 py-2">{v.payeeName}</td>
              <td className="px-3 py-2 text-gray-500">{v.reference}</td>
              <td className="px-3 py-2 text-right">{thousandSeperatorWithPrefix(v.amountDue)}</td>
              <td className="px-3 py-2">{formatDate(v.dueDate)}</td>
              <td className="px-3 py-2">{v.recurring ? v.frequency : 'One-off'}</td>
              <td className="px-3 py-2">
                {v.escrowed ? 'Yes' : <span className="text-gray-500">Borrower pays</span>}
              </td>
              <td className="px-3 py-2">
                <span className="flex gap-2">
                  <span className="cursor-pointer text-shade-blue" onClick={() => setEditing(v)}>
                    <PencilSquareIcon className="w-4 h-4" />
                  </span>
                  {v.active && (
                    <span className="cursor-pointer text-red-600" onClick={() => onDeactivate(v)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        {vouchers.length > 0 && (
          <TotalsRow
            tdClass="px-3 py-2"
            cells={['Totals', '', '', sumBy(vouchers, 'amountDue'), '', '', '', '']}
          />
        )}
      </table>

      {/* A preview of what the escrow analysis will be built from, so the vouchers can be sanity
          checked against real bills before any statement depends on them. */}
      <div className="mt-6">
        <p className="font-semibold">Projected escrow disbursements — next 12 months</p>
        <p className="text-sm text-gray-500 mb-2">
          From {formatDate(projectionStart)}. Escrowed vouchers only.
        </p>
        {projection.disbursements.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing projected in this window.</p>
        ) : (
          <>
            <table className="text-[14px] text-left text-gray-900">
              <tbody>
                {projection.disbursements.map((d, i) => (
                  <tr key={`${d.voucherId}-${i}`} className="border-b">
                    <td className="pr-6 py-1">{formatDate(d.date)}</td>
                    <td className="pr-6 py-1">{d.type}</td>
                    <td className="pr-6 py-1 text-gray-500">{d.payeeName}</td>
                    <td className="py-1 text-right">{thousandSeperatorWithPrefix(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[14px]">
              <span className="mr-6">
                Annual total: <span className="font-semibold">{thousandSeperatorWithPrefix(projection.total)}</span>
              </span>
              <span>
                Base monthly escrow: <span className="font-semibold">{thousandSeperatorWithPrefix(monthly)}</span>
              </span>
            </div>
          </>
        )}
      </div>

      {/* Analyses already run. Each keeps the figures it was run with, which is what its
          statement prints — so an older run stays truthful after the vouchers move on. */}
      {analyses.length > 0 && (
        <div className="mt-6">
          <p className="font-semibold">Escrow analyses run</p>
          <table className="text-[14px] text-left text-gray-900 mt-1">
            <thead className="text-gray-600 text-[13px]">
              <tr>
                <th className="pr-6 py-1">Computation Year</th>
                <th className="pr-6 py-1 text-right">New Monthly</th>
                <th className="pr-6 py-1 text-right">Surplus</th>
                <th className="pr-6 py-1 text-right">Shortage</th>
                <th className="pr-6 py-1">Run</th>
                <th className="pr-6 py-1">Applied</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {analyses.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="pr-6 py-1">
                    {formatDate(a.computationYearStart)} – {formatDate(a.computationYearEnd)}
                  </td>
                  <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(a.newMonthlyEscrow)}</td>
                  <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(a.surplus)}</td>
                  <td className="pr-6 py-1 text-right">{thousandSeperatorWithPrefix(a.shortage)}</td>
                  <td className="pr-6 py-1 text-gray-500">{formatDate(a.createdAt)}</td>
                  <td className="pr-6 py-1">
                    {a.appliedStatus ? (
                      <span className="text-xs text-green-700">
                        From {formatDate(a.appliedEffectiveDate)}
                        {a.appliedStatus === 'scheduled' ? ' (scheduled)' : ''}
                      </span>
                    ) : canApply ? (
                      <button
                        type="button"
                        className="text-shade-blue hover:underline text-xs"
                        onClick={() =>
                          setApplying({ id: a.id, effectiveDate: formatDate(a.computationYearStart, 'yyyy-MM-dd') })
                        }
                      >
                        Apply
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Not applied</span>
                    )}
                  </td>
                  <td className="py-1">
                    <button
                      type="button"
                      className="text-shade-blue hover:underline inline-flex items-center gap-1 text-xs"
                      disabled={downloading === a.id}
                      onClick={() => onDownloadStatement(a.id)}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      {downloading === a.id ? 'Preparing…' : 'Statement'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {applying && (
        <Modal
          title="Apply Escrow Analysis"
          titleOkay="Apply"
          loading={applyBusy}
          onClose={() => setApplying(null)}
          onOk={onApply}
          isOpen
        >
          <div className="w-120 text-gray-900 mt-2 mb-3">
            <div className="text-[13px] text-gray-500 mb-1">Effective with the payment due</div>
            <input
              type="date"
              className="text-[14px] border rounded px-2 py-2"
              value={applying.effectiveDate}
              onChange={(e) => setApplying({ ...applying, effectiveDate: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-2">
              The new monthly escrow is billed from this payment on: statements, amount due, ACH drafts and
              quotes all use it, and Impound Monthly on Servicing Information updates on the day. It is listed,
              and can be cancelled before it takes effect, on Rate &amp; Payment Changes.
            </p>
          </div>
        </Modal>
      )}

      {analyzing && (
        <EscrowAnalysisDialog
          defaultStart={projectionStart}
          currentMonthlyEscrow={escrowDefaults.currentMonthlyEscrow}
          escrowBalance={escrowDefaults.startingBalance}
          onClose={() => setAnalyzing(false)}
          onSaved={load}
        />
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Update Voucher' : 'Add Voucher'}
          titleOkay="Save"
          isOpen
          loading={saving}
          onClose={() => setEditing(null)}
          onOk={onSubmit}
        >
          <div className="w-96 grid grid-cols-2 gap-4 mt-2">
            <Select2
              id="voucher-type"
              title="Type"
              options={TYPES}
              value={editing.type || 'Taxes'}
              onChange={(v: any) => set('type', v)}
            />
            <Input2 title="Payee" value={editing.payeeName || ''} onChange={(v: any) => set('payeeName', v)} />
            <Input2
              title="Reference"
              value={editing.reference || ''}
              onChange={(v: any) => set('reference', v)}
            />
            <Input2
              title="Amount Due"
              type="thousandSep"
              prefix="$"
              value={String(editing.amountDue ?? '')}
              onChange={(v: any) => set('amountDue', v)}
            />
            <Input2
              title="Date Due"
              type="date"
              value={editing.dueDate || ''}
              onChange={(v: any) => set('dueDate', v)}
            />
            <Select2
              id="voucher-recurring"
              title="Recurring"
              options={{ yes: 'Yes', no: 'One-off' }}
              value={editing.recurring ? 'yes' : 'no'}
              onChange={(v: any) => set('recurring', v === 'yes')}
            />
            {editing.recurring && (
              <Select2
                id="voucher-frequency"
                title="Frequency"
                options={FREQUENCIES}
                value={editing.frequency || 'Annual'}
                onChange={(v: any) => set('frequency', v)}
              />
            )}
            <Select2
              id="voucher-escrowed"
              title="Paid From"
              options={{ escrow: 'Escrow', borrower: 'Borrower directly' }}
              value={editing.escrowed === false ? 'borrower' : 'escrow'}
              onChange={(v: any) => set('escrowed', v === 'escrow')}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}
